import { useState, type MouseEvent } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import MenuIcon from '@mui/icons-material/Menu';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TuneIcon from '@mui/icons-material/Tune';
import CheckIcon from '@mui/icons-material/Check';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppTheme } from '../theme/AppThemeProvider';
import type { Breadcrumb, CurrentUser, Role, UserContext } from './types';

interface TopbarProps {
  pageTitle?: string;
  breadcrumbs?: Breadcrumb[];
  pageActions?: React.ReactNode;
  user?: CurrentUser;
  userContext?: UserContext;
  availableRoles?: Role[];
  onNavigate: (to: string) => void;
  onOpenMobileNav: () => void;
  onSignOut?: () => void;
  onChangeRole?: (role: Role) => void;
  onOpenContextPicker?: () => void;
}

function initials(user?: CurrentUser): string {
  const source = user?.displayName?.trim() || user?.username?.trim() || '';
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin',
  'operation-manager': 'Operation manager',
  manager: 'Manager',
  supervisor: 'Supervisor',
  approver: 'Approver',
  'chief-approver': 'Chief approver',
  employee: 'Employee',
  secretary: 'Secretary',
  management: 'Management',
};

export function Topbar({
  pageTitle,
  breadcrumbs,
  pageActions,
  user,
  userContext,
  availableRoles = [],
  onNavigate,
  onOpenMobileNav,
  onSignOut,
  onChangeRole,
  onOpenContextPicker,
}: TopbarProps) {
  const theme = useTheme();
  const { mode, toggleMode } = useAppTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [userAnchor, setUserAnchor] = useState<HTMLElement | null>(null);
  const [roleAnchor, setRoleAnchor] = useState<HTMLElement | null>(null);

  // Only worth offering a switcher when there is something to switch between.
  const canSwitchRole = Boolean(onChangeRole) && availableRoles.length > 1;

  const contextParts = [userContext?.hospital, userContext?.section].filter(Boolean) as string[];

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar,
        height: theme.custom.layout.topbarHeight,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: { xs: 1.5, md: 2.5 },
        bgcolor: 'background.paper',
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      {isMobile && (
        <IconButton onClick={onOpenMobileNav} aria-label="Open navigation" edge="start">
          <MenuIcon />
        </IconButton>
      )}

      <Box sx={{ minWidth: 0, flex: 1 }}>
        {breadcrumbs && breadcrumbs.length > 0 && (
          <Breadcrumbs
            aria-label="Breadcrumb"
            sx={{ '& .MuiBreadcrumbs-separator': { mx: 0.75 }, mb: pageTitle ? -0.25 : 0 }}
          >
            {breadcrumbs.map((crumb, i) =>
              crumb.to && i < breadcrumbs.length - 1 ? (
                <Link
                  key={`${crumb.label}-${i}`}
                  component="button"
                  underline="hover"
                  color="text.secondary"
                  variant="caption"
                  onClick={() => onNavigate(crumb.to!)}
                  sx={{ border: 0, background: 'none', p: 0, cursor: 'pointer' }}
                >
                  {crumb.label}
                </Link>
              ) : (
                <Typography key={`${crumb.label}-${i}`} variant="caption" color="text.secondary">
                  {crumb.label}
                </Typography>
              ),
            )}
          </Breadcrumbs>
        )}
        {pageTitle && (
          <Typography variant="h3" noWrap component="h1">
            {pageTitle}
          </Typography>
        )}
      </Box>

      <Stack direction="row" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
        {pageActions}

        {/* Hospital / section / role. The legacy apps kept this in cookies and
            showed it nowhere, so users could not tell which context they were in. */}
        {(contextParts.length > 0 || userContext?.role) && (
          <Chip
            size="small"
            variant="outlined"
            icon={<TuneIcon sx={{ fontSize: 15 }} />}
            onClick={onOpenContextPicker}
            label={
              <Stack direction="row" gap={0.75} alignItems="center">
                {userContext?.role && (
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {ROLE_LABELS[userContext.role]}
                  </Box>
                )}
                {contextParts.length > 0 && (
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {contextParts.join(' · ')}
                  </Box>
                )}
              </Stack>
            }
            sx={{ display: { xs: 'none', sm: 'flex' }, maxWidth: 340 }}
          />
        )}

        <Tooltip title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
          <IconButton onClick={toggleMode} aria-label="Toggle colour mode" size="small">
            {mode === 'light' ? (
              <DarkModeOutlinedIcon fontSize="small" />
            ) : (
              <LightModeOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title={user?.displayName ?? user?.username ?? 'Account'}>
          <IconButton
            onClick={(e: MouseEvent<HTMLElement>) => setUserAnchor(e.currentTarget)}
            size="small"
            aria-haspopup="menu"
            aria-label="Account menu"
          >
            <Avatar
              src={user?.avatarUrl}
              sx={{ width: 30, height: 30, fontSize: '0.75rem', bgcolor: 'primary.main' }}
            >
              {initials(user)}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Stack>

      <Menu
        anchorEl={userAnchor}
        open={Boolean(userAnchor)}
        onClose={() => setUserAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 230 } } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.displayName ?? user?.username ?? 'Not signed in'}
          </Typography>
          {user?.email && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {user.email}
            </Typography>
          )}
        </Box>
        <Divider />

        {canSwitchRole && (
          <MenuItem
            onClick={(e: MouseEvent<HTMLElement>) => {
              setUserAnchor(null);
              setRoleAnchor(e.currentTarget);
            }}
          >
            <ListItemIcon>
              <SwapHorizIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Switch role" primaryTypographyProps={{ variant: 'body2' }} />
          </MenuItem>
        )}

        {onOpenContextPicker && (
          <MenuItem
            onClick={() => {
              setUserAnchor(null);
              onOpenContextPicker();
            }}
          >
            <ListItemIcon>
              <TuneIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Change hospital / section"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        )}

        {onSignOut && [
          <Divider key="divider" />,
          <MenuItem
            key="signout"
            onClick={() => {
              setUserAnchor(null);
              onSignOut();
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText
              primary="Sign out"
              primaryTypographyProps={{ variant: 'body2', color: 'error.main' }}
            />
          </MenuItem>,
        ]}
      </Menu>

      <Menu
        anchorEl={roleAnchor}
        open={Boolean(roleAnchor)}
        onClose={() => setRoleAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        <Typography variant="overline" color="text.secondary" sx={{ px: 2, display: 'block' }}>
          Your roles here
        </Typography>
        {availableRoles.map(role => (
          <MenuItem
            key={role}
            selected={role === userContext?.role}
            onClick={() => {
              setRoleAnchor(null);
              onChangeRole?.(role);
            }}
          >
            <ListItemIcon>
              {role === userContext?.role ? <CheckIcon fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText
              primary={ROLE_LABELS[role]}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
